/**
 * DashboardLayout.tsx — the shared shell for the Manager Metrics Dashboard (MD-F01/F02). Renders the
 * three-tab bar (Overview · Tooling · Learning) and the shared period + department filter bar, with
 * an <Outlet/> for the active page. The filters are URL-driven (`?period` + `?department`) exactly
 * like the catalogue: submitting the <Form method="get"> reloads the child loader with new query
 * params — there is no client filter state to drift.
 *
 * The layout loader fetches only the department options (GET /api/tools/refdata, non-fatal). The
 * Manager|PlatformOwner gate and the actual metric fetches live in each child page's own loader, so a
 * non-manager who reaches /dashboard by URL sees the page's NotPermitted, not a broken shell.
 */
import { lazy, Suspense } from 'react';
import { Form, NavLink, Outlet, useLoaderData, useSearchParams } from 'react-router';
import { apiGet, fetchTeamScopeOptions, type TeamScopeOption } from './api';
import type { RefOption, ToolRefData } from '../tools/api';
import { ProvisionalBanner } from '../admin/shared';
import { DASHBOARD_STYLES } from './shared';

/**
 * The animated Topography ripple, CODE-SPLIT exactly as the /me profile and /admin bands split it
 * (it pulls in `ogl`, most of a ~250kB chunk), so `lazy` keeps it off every route that has no band.
 * Safe to defer: the band is purely decorative and takes no layout space — the topo is
 * `position:absolute; inset:0; pointer-events:none` inside the band (.tb-dash-band__topo), so the
 * `null` Suspense fallback renders nothing, shifts nothing, and blocks no interaction.
 */
const Topography = lazy(() => import('../design/Topography'));

export interface DashboardLayoutData {
  departments: RefOption[];
  /** Teams the caller may scope the Learning page to (0049). Empty ⇒ the team selector is not shown. */
  teams: TeamScopeOption[];
}

export async function dashboardLayoutLoader(): Promise<DashboardLayoutData> {
  // Non-fatal, like the catalogue: if refdata fails the department filter simply shows no options.
  // The team options are fetched in parallel and are equally non-fatal — fetchTeamScopeOptions never
  // throws, so a manager with no team (or a metrics outage) just gets no team selector.
  const [refRes, teams] = await Promise.all([
    apiGet<ToolRefData>('/api/tools/refdata'),
    fetchTeamScopeOptions(),
  ]);
  return { departments: refRes.kind === 'ok' ? refRes.body.departments : [], teams };
}

const PERIODS: { value: string; label: string }[] = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '180', label: 'Last 6 months' },
];

export function DashboardLayout() {
  const { departments, teams } = useLoaderData() as DashboardLayoutData;
  const [params] = useSearchParams();
  const period = params.get('period') ?? '30';
  const department = params.get('department') ?? '';
  const teamId = params.get('teamId') ?? '';

  // `admin-surface` scopes the legible darker --text-inactive (design-system.css) to this subtree, and
  // `font-sans` selects the design system's Inter, exactly as AdminHome's root does. DASHBOARD_STYLES is
  // now self-contained (teal tokens), so the old navy ADMIN_STYLES import is gone.
  return (
    <section
      aria-labelledby="dashboard-heading"
      className="tb-admin tb-dash-page admin-surface font-sans"
    >
      <style>{DASHBOARD_STYLES}</style>
      {/* Hero band — the charcoal sibling of the /me profile and /admin headers, carrying the SAME
          animated Topography ripple but recoloured white ("carte blanche") against the dark band.
          Full-bleed, flush under the app header. The heading stays the labelled <h1> for the
          section's aria-labelledby. */}
      <div className="tb-dash-band-bleed">
        <div className="tb-dash-band">
          <Suspense fallback={null}>
            <Topography
              className="tb-dash-band__topo"
              lowColor="#ffffff"
              midColor="#ffffff"
              highColor="#ffffff"
              fadeCenter={[0.5, 0.5]}
              fadeInner={0}
              fadeOuter={1.5}
              fadeAspect={1.9}
              aspectCorrect={1}
              opacity={0.4}
              glow={0.3}
            />
          </Suspense>
          <div className="tb-dash-band__cluster">
            <h1 id="dashboard-heading" className="tb-dash-band__title">
              Management dashboard
            </h1>
            <p className="tb-dash-band__subtitle">Read-only reporting for managers</p>
          </div>
        </div>
      </div>
      <ProvisionalBanner />
      <p className="tb-muted" style={{ maxWidth: '68ch' }}>
        Platform-wide, read-only reporting for managers. Every figure here is an aggregate — it
        names nobody and ranks no person, and people-counts are only shown once enough distinct
        individuals are involved that the number cannot identify anyone. The dashboard writes
        nothing.
      </p>

      <nav aria-label="Dashboard sections">
        <ul className="tb-dash-tabs">
          <li>
            <NavLink to={{ pathname: '/dashboard/legacy', search: params.toString() }} end>
              Overview
            </NavLink>
          </li>
          <li>
            <NavLink to={{ pathname: '/dashboard/legacy/tooling', search: params.toString() }}>
              Tooling
            </NavLink>
          </li>
          <li>
            <NavLink to={{ pathname: '/dashboard/legacy/learning', search: params.toString() }}>
              Learning
            </NavLink>
          </li>
        </ul>
      </nav>

      <Form method="get" className="tb-dash-filters" role="search" aria-label="Filter metrics">
        <label className="tb-field">
          <span>Time period</span>
          <select name="period" defaultValue={period}>
            {PERIODS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
        <label className="tb-field">
          <span>Department</span>
          <select name="department" defaultValue={department}>
            <option value="">All departments</option>
            {departments.map((d) => (
              <option key={d.id} value={String(d.id)}>
                {d.label}
              </option>
            ))}
          </select>
        </label>
        {/* Team scope (0049) — only rendered when the caller has a team to pick (a manager sees the
            team they manage; an owner sees every team). It narrows every tab's people-shaped figures to
            one team's roster, still as aggregates and still cohort-suppressed: Overview's platform-health
            figures and active-people graph, Tooling's adoption counts, and Learning's mandatory-training
            figures. Tool- and department-cross-tab figures ignore it (they carry no team meaning). A team
            below the cohort floor simply reports nothing, exactly as a small department does. */}
        {teams.length > 0 && (
          <label className="tb-field">
            <span>Team</span>
            <select name="teamId" defaultValue={teamId}>
              <option value="">No team scope</option>
              {teams.map((t) => (
                <option key={t.teamId} value={t.teamId}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <button type="submit" className="tb-submit">
          Apply
        </button>
      </Form>

      <Outlet />
    </section>
  );
}
